/**
 * PATH: packages/tenant-shared/src/lib/toastVariants.ts
 * FILE_ID: TENANTSHARED-LIB-TOAST-VARIANTS-001
 * PURPOSE: Mirror of `packages/ui/src/lib/toastVariants.ts`. See
 *          that file for the full rationale.
 *
 * WHY a mirror not a re-export:
 *   `packages/tenant-shared` does not depend on `packages/ui`
 *   (and shouldn't — the dependency graph runs the other way
 *   for some hubs). The two copies must stay byte-identical;
 *   the regression tests in BOTH packages exercise the same
 *   contract so drift is caught at CI time.
 *
 * INVARIANTS — identical to packages/ui/src/lib/toastVariants.ts:
 *   1. Opaque background classes (no alpha modifier).
 *   2. WCAG-AA contrast text against the background.
 *   3. Visible (non-alpha) border one shade darker than the bg.
 *
 * RELATED:
 *   - packages/ui/src/lib/toastVariants.ts (canonical)
 *   - packages/tenant-shared/src/components/Toast.tsx
 *   - packages/email-hub/src/_internal/components/Toast.tsx
 *   - docs/bugs/BUG_LEDGER.md  → BUG-2026-05-20-TOAST-CONTRAST
 */

export type ToastVariantKey = 'success' | 'error' | 'warning' | 'info'

export const TOAST_VARIANT_CLASSES: Record<ToastVariantKey, string> = {
  success: 'border-emerald-500 bg-emerald-600 text-white',
  error: 'border-red-500 bg-red-600 text-white',
  warning: 'border-amber-500 bg-amber-400 text-amber-950',
  info: 'border-blue-500 bg-blue-600 text-white',
}

export function getToastVariantClasses(type: ToastVariantKey | string): string {
  const cls = TOAST_VARIANT_CLASSES[type as ToastVariantKey]
  if (cls) return cls
  if (import.meta.env.MODE !== 'production') {
    // eslint-disable-next-line no-console
    console.error(
      '[toastVariants] Unknown variant "%s" — falling back to opaque slate. ' +
      'Allowed variants: %s.',
      type,
      Object.keys(TOAST_VARIANT_CLASSES).join(', '),
    )
  }
  return 'border-slate-700 bg-slate-800 text-white'
}

export function assertOpaqueToastClass(cls: string, label = 'variant'): void {
  const tokens = cls.split(/\s+/).filter(Boolean)
  const bg = tokens.find((t) => t.startsWith('bg-'))
  if (!bg) {
    throw new Error(`[toastVariants] ${label}: missing bg-* class in "${cls}"`)
  }
  if (/\/\d+$/.test(bg)) {
    throw new Error(
      `[toastVariants] ${label}: background "${bg}" uses an alpha ` +
      `modifier — that renders as invisible white-on-white on light ` +
      `host pages. Use an opaque bg-X-600 (or similar) instead. ` +
      `See BUG-2026-05-20-TOAST-CONTRAST.`,
    )
  }
  const fg = tokens.find((t) => t.startsWith('text-'))
  if (!fg) {
    throw new Error(`[toastVariants] ${label}: missing text-* class in "${cls}"`)
  }
  if (/^text-(emerald|red|amber|blue|sky|indigo|cyan|teal|lime|orange)-(200|300|400)$/.test(fg)) {
    throw new Error(
      `[toastVariants] ${label}: foreground "${fg}" is a pastel that ` +
      `bleeds into light AND dark host pages. Use text-white or ` +
      `text-X-950 against a saturated bg instead. ` +
      `See BUG-2026-05-20-TOAST-CONTRAST.`,
    )
  }
}
